import {
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
} from "firebase/auth";
import { setPostHogBrand } from "../../posthog";
import { clearSandboxView } from "../../features/sandbox/sandboxView";
const apiUrl = import.meta.env.VITE_FIREBASE_API_URL;
import { useUserStore } from "../../store/userStore";
import { type i18n as I18nType, type TFunction } from "i18next";
import { auth } from "../../firebaseConfig";
import { FirebaseError } from "firebase/app";
import { type CustomAlertProps } from "../../components/base/CustomAlert";
import { isPlatformAdminRoleId } from "../../hooks/useIsReseller";
import { resolveLandingRoute } from "../../config/appsConfig";
import { clearNextPath, getNextPath } from "../../utils/nextPath";
import {
  TRUCK_YARD_ADMIN_ROLE_ID,
  TRUCK_YARD_INSPECTOR_ROLE_ID,
  TRUCK_YARD_GUARD_ROLE_ID,
  PTAR_COMPANY_ID,
  // Legacy alias
  TRUCK_YARD_SUPERVISOR_ROLE_ID,
} from "../../hooks/useTruckYardRole";
import { WEB_SERVICE_ROLE_ID } from "../../hooks/useIsWebServiceRole";
import Swal from "sweetalert2";
import api from "../../utils/api";
import { isCompanySuspendedError } from "../../utils/apiErrorCodes";
import { useAccessStore } from "../../store/accessStore";
import axios from "axios";
import { requestSecurityChallenge } from "../../security/securityChallengeStore";
import { clearSecurityStatus } from "../../security/securityStatus";
import {
  validateGuardSupervisorAccess,
  requestLocationPermission,
} from "../../utils/accessValidation";
import { brand } from "@/config/theme";
import { waitAtMost } from "./waitAtMost";
import { ACCESS_WAIT_MS, preloadRouteChunks } from "./postLoginWarmup";
import { normalizeAccessLayers } from "../../services/access";

import { DIALOG_COLORS } from "../../components/ui";
import { clearDemoToken, getDemoToken } from "../../utils/demoInvite";
import {
  trackLoginSucceeded,
  trackLoginFailed,
  trackLoginBlocked,
  trackLogoutCompleted,
  trackPasswordResetRequested,
  type AuthMethod,
} from "../../analytics/authAnalytics";

export const logoutUser = async () => {
  const { setError, setUser } = useUserStore.getState();
  // Un `next` de vincular (CC-CONSOLE#1561) es de esta persona: no lo hereda
  // quien entre después en la misma pestaña.
  clearNextPath();
  try {
    const token = localStorage.getItem("token");
    const sessionId = localStorage.getItem("sessionId");

    const fcmToken = localStorage.getItem("tokenCFM");
    const userId = localStorage.getItem("userId");

    // Las dos llamadas no dependen una de otra: van en paralelo y el logout
    // dura lo de la más lenta, no la suma. Se espera por AMBAS (sin tope): si
    // la recarga de abajo cortara el cierre de la sesión, ésta quedaría abierta
    // en el servidor y el siguiente inicio de sesión toparía con el aviso de
    // «sesión activa». Cada una atrapa su error: un fallo no impide salir.
    const unsubscribeFcm =
      token && sessionId && fcmToken && userId
        ? axios
            .post(
              `${apiUrl}/notifications/unsuscribe`,
              { tokenCFM: fcmToken, id: userId },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  "x-session-id": sessionId,
                  "x-user-id": userId,
                },
              },
            )
            .catch((error) => {
              console.error("Error unsubscribing FCM token on logout:", error);
            })
        : Promise.resolve();

    const closeSession =
      sessionId && token
        ? api
            .put(
              `${apiUrl}/session/close`,
              {},
              {
                params: { id: sessionId, sessionId: sessionId },
                headers: { Authorization: `Bearer ${token}` },
              },
            )
            .catch((error) => {
              console.error("Error closing session in backend:", error);
            })
        : Promise.resolve();

    await Promise.all([unsubscribeFcm, closeSession]);

    await signOut(auth);

    localStorage.removeItem("token");
    localStorage.removeItem("sessionId");
    localStorage.removeItem("lastRoute");
    localStorage.removeItem("companyId");
    localStorage.removeItem("userCompanyId");
    localStorage.removeItem("userRole");
    localStorage.removeItem("userId");
    useAccessStore.getState().clear();
    // El estado de seguridad (2FA) es de ESTA cuenta.
    clearSecurityStatus();
    sessionStorage.removeItem("security:grace-notice");
    localStorage.removeItem("vehicle-route-tracking");
    localStorage.removeItem("vehicle-footprint-tracking");
    localStorage.removeItem("tokenCFM");
    localStorage.removeItem("tokenCFM_domain");
    localStorage.removeItem("tokenCFM_userId");

    // ANTES del `setUser(null)`: esa asignacion dispara el `reset()` de
    // PostHog en identity.ts, y despues de un reset el evento se atribuiria a
    // un distinct_id anonimo nuevo en vez de a quien acaba de salir.
    trackLogoutCompleted();

    setUser(null);
    window.location.href = "/";
  } catch (err) {
    setError("Error al cerrar sesión");
    console.error("Logout error:", err);
  }
};

// Helper function to check if input is email
const isEmail = (input: string): boolean => {
  const emailRegex = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;
  return emailRegex.test(input);
};

// Helper function to get email by username
const getEmailFromUsername = async (
  username: string,
): Promise<string | null> => {
  try {
    const res = await api.get(
      `${apiUrl}/user/email-by-username?username=${encodeURIComponent(username)}`,
    );

    const email = res.data?.data?.email || res.data?.email || null;
    return email;
  } catch (error) {
    return null;
  }
};

interface LoginProfile {
  user?: any;
  roleName?: string;
  companyName?: string;
  /** Marca de la compañía (utils/brands) para PostHog (CC-CONSOLE#1538). */
  companyBrand?: string | null;
  /** Accesos efectivos (misma forma que GET /access/me); sólo los manda el backend actual. */
  access?: unknown;
}

/**
 * Ruta a la que se redirige tras el login, según rol y compañía. Los usuarios
 * estándar aterrizan en la pantalla de inicio configurada (defaultLanding) o en
 * el grid de apps (/apps) vía resolveLandingRoute() en el caso por defecto.
 */
const resolvePostLoginRoute = (
  roleId: string,
  roleName: string,
  companyId: string,
): string => {
  const isPTARCompany = companyId === PTAR_COMPANY_ID;

  if (roleId === WEB_SERVICE_ROLE_ID) return "/map";
  if (roleName === "web_services") return "/admin/units";
  // Admin PTAR: acceso completo a truck-yard e inspecciones
  if (isPTARCompany && roleId === TRUCK_YARD_ADMIN_ROLE_ID) {
    return "/truck-yard/access";
  }
  // Inspector PTAR: solo inspecciones
  if (isPTARCompany && roleId === TRUCK_YARD_INSPECTOR_ROLE_ID) {
    return "/inspecciones";
  }
  // Guard PTAR: solo accesos en truck-yard
  if (isPTARCompany && roleId === TRUCK_YARD_GUARD_ROLE_ID) {
    return "/truck-yard/access";
  }
  // Otros supervisores/guardias (no PTAR) van al patio de maniobras
  if (
    roleId === TRUCK_YARD_SUPERVISOR_ROLE_ID ||
    roleId === TRUCK_YARD_GUARD_ROLE_ID
  ) {
    return "/truck-yard/access";
  }
  return resolveLandingRoute();
};

/**
 * Perfil para backends anteriores, cuyo /login no trae `profile`: usuario, rol
 * y compañía por GET. El rol y la compañía no dependen entre sí, así que van en
 * paralelo. Un fallo se registra y deja ese dato vacío; nunca tumba el login.
 */
const fetchLegacyProfile = async (
  email: string | null | undefined,
  token: string,
  companyId?: string,
): Promise<LoginProfile> => {
  const profile: LoginProfile = {};
  if (!email) return profile;
  const headers = { Authorization: `Bearer ${token}` };

  try {
    const userRes = await api.get(
      `${apiUrl}/user?email=${encodeURIComponent(email)}`,
      { headers },
    );
    const body = userRes.data;
    if (body && (body.user || body.data)) profile.user = body.user || body.data;

    const roleId = profile.user?.role_id;
    if (!roleId) return profile;

    const [roleRes, companyRes] = await Promise.all([
      api
        .get(`${apiUrl}/role?id=${encodeURIComponent(roleId)}`, { headers })
        .catch((e) => {
          console.error("Error fetching role:", e);
          return null;
        }),
      companyId
        ? api
            .get(`${apiUrl}/company?id=${encodeURIComponent(companyId)}`)
            .catch((e) => {
              console.error("Error fetching company name:", e);
              return null;
            })
        : null,
    ]);

    profile.roleName = roleRes?.data?.data?.data?.name || "";
    // Handle nested response: data.data.data or data.data
    const companyRaw = companyRes?.data?.data;
    const companyData = companyRaw?.data || companyRaw;
    profile.companyName = companyData?.name || companyData?.business_name || "";
    profile.companyBrand = companyData?.brand ?? null;
  } catch (e) {
    console.error("Error fetching user:", e);
  }
  return profile;
};

export const authenticateUser = async (
  emailOrUsername: string,
  password: string,
  navigate: any,
  showAlert: (alert: CustomAlertProps) => void,
  t: TFunction<"translation", undefined>,
) => {
  const { setUser, setLoading, setError } = useUserStore.getState();
  setLoading(true);
  setError(null);

  // Este archivo maneja correos, usuarios y contraseñas. NADA de eso sale en
  // los eventos: solo el metodo, el codigo de error de Firebase y el motivo de
  // bloqueo, que son enumeraciones cerradas.
  const method: AuthMethod = "password";
  // ¿Hay sesión de Firebase abierta sin sesión de consola detrás?
  let firebaseSessionOpen = false;

  try {
    // Determine if input is email or username
    let email = emailOrUsername;

    if (!isEmail(emailOrUsername)) {
      // Input is username, get the associated email
      const fetchedEmail = await getEmailFromUsername(emailOrUsername);
      if (!fetchedEmail) {
        showAlert({
          title: t("errorLogin"),
          text: t("invalidUsernameOrPassword"),
          icon: "error",
          confirmButtonText: t("accept"),
        });
        trackLoginBlocked("username_not_found");
        setError(t("invalidUsernameOrPassword"));
        setLoading(false);
        return;
      }
      email = fetchedEmail;
    }

    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password,
    );
    // A partir de aquí hay sesión de FIREBASE viva aunque el backend todavía no
    // haya dicho nada. Si el login no llega a persistir sesión de consola, hay
    // que cerrarla (ver la red de seguridad del `catch`).
    firebaseSessionOpen = true;
    const idToken = await userCredential.user.getIdToken();

    // Primera llamada a /login con checkOnly: devuelve el rol sin crear sesión.
    // `continueIfNoAccessCheck` pide al backend que, si el rol NO exige validar
    // acceso (todos salvo patio), siga con el login completo en esta misma
    // llamada y se ahorre la segunda. Un backend anterior ignora el campo y
    // responde el `checkOnly` de siempre. La invitación a una demo
    // (`?demo=<token>`) viaja desde aquí: sólo la lee la rama de login real.
    const demoToken = getDemoToken();
    const checkRes = await api.post(`${apiUrl}/login`, {
      idToken,
      checkOnly: true,
      continueIfNoAccessCheck: true,
      ...(demoToken ? { demoToken } : {}),
    });
    const firstIsFullLogin =
      Boolean(checkRes.data?.data?.token) ||
      checkRes.data?.data?.hasActiveSession === true;
    const userData = firstIsFullLogin ? null : checkRes.data?.data?.data;

    if (!firstIsFullLogin && !userData) {
      trackLoginBlocked("user_data_missing");
      await signOut(auth);
      setError(t("errorLogin"));
      setLoading(false);
      return;
    }

    // Validar acceso para guardias y supervisores (geolocalización y horario)
    const requiresAccessValidation =
      !firstIsFullLogin &&
      (userData.role_id === TRUCK_YARD_GUARD_ROLE_ID ||
        userData.role_id === TRUCK_YARD_SUPERVISOR_ROLE_ID ||
        userData.role_id === TRUCK_YARD_INSPECTOR_ROLE_ID);

    if (requiresAccessValidation) {
      // Mostrar explicación y solicitar permisos de ubicación
      const permissionRequest = await Swal.fire({
        title: t("locationPermissionRequired"),
        text: t("locationPermissionDesc"),
        icon: "info",
        showCancelButton: true,
        confirmButtonText: t("accept"),
        cancelButtonText: t("cancel"),
        allowOutsideClick: false,
        allowEscapeKey: false,
        confirmButtonColor: brand.primary,
        cancelButtonColor: DIALOG_COLORS.cancel,
      });

      if (!permissionRequest.isConfirmed) {
        // Usuario canceló, cerrar sesión de Firebase y salir (sin sesión backend)
        await signOut(auth);
        setError(t("locationPermissionDenied"));
        setLoading(false);
        Swal.fire({
          title: t("locationPermissionDenied"),
          text: t("locationPermissionDeniedDesc"),
          icon: "error",
          confirmButtonText: t("accept"),
          confirmButtonColor: brand.primary,
        });
        return;
      }

      // Solicitar permisos de ubicación explícitamente
      const locationPermission = await requestLocationPermission();

      if (!locationPermission) {
        // Usuario negó ubicación, cerrar sesión de Firebase y salir (sin sesión backend)
        await signOut(auth);
        setError(t("locationPermissionDenied"));
        setLoading(false);
        Swal.fire({
          title: t("locationPermissionDenied"),
          text: t("locationPermissionDeniedDesc"),
          icon: "error",
          confirmButtonText: t("accept"),
          confirmButtonColor: brand.primary,
        });
        return;
      }

      // Validar geocercas y horario antes de crear sesión
      Swal.fire({
        title: t("validatingAccess"),
        text: t("checkingLocation"),
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const accessResult = await validateGuardSupervisorAccess(
        {
          yard_id: userData.yard_id,
          yard_ids: userData.yard_ids,
          schedule: userData.schedule,
        },
        idToken, // Usar el Firebase idToken para la validación
      );

      Swal.close();

      if (!accessResult.allowed) {
        await signOut(auth);
        let title = t("accessDenied");
        let text = "";

        switch (accessResult.reason) {
          case "outside_geofence":
            title = t("outsideGeofence");
            text = t("outsideGeofenceDesc", {
              yardName: accessResult.yardName || "el patio",
            });
            break;
          case "outside_schedule":
            title = t("outsideSchedule");
            text = t("outsideScheduleDesc", {
              from: accessResult.scheduleInfo?.from || "",
              to: accessResult.scheduleInfo?.to || "",
              currentTime: accessResult.scheduleInfo?.currentTime || "",
            });
            break;
          case "no_yard_assigned":
            title = t("noYardAssigned");
            text = t("noYardAssignedDesc");
            break;
          case "geolocation_error":
            title = t("geolocationError");
            text = t("geolocationErrorDesc");
            break;
        }

        // El motivo, no el texto traducido: el mensaje interpola el nombre del
        // patio y el horario del turno. Un pico aqui es un problema de
        // configuracion operativa, no de contraseñas olvidadas.
        trackLoginBlocked("access_denied", accessResult.reason || "unknown");
        setError(title);
        setLoading(false);
        await Swal.fire({
          title,
          text,
          icon: "warning",
          confirmButtonText: t("accept"),
          confirmButtonColor: brand.primary,
          allowOutsideClick: false,
          allowEscapeKey: false,
        });
        return;
      }
    }

    // Si la primera llamada no fue ya el login completo (rol de patio, o un
    // backend anterior), se llama /login normal para crear la sesión.
    const res = firstIsFullLogin
      ? checkRes
      : await api.post(`${apiUrl}/login`, {
          idToken,
          ...(demoToken ? { demoToken } : {}),
        });

    const data = res.data;
    // De un solo uso: si ya entró a una compañía, el token no se reenvía.
    if (demoToken && data.data?.companyId) clearDemoToken();

    // Verificar si hay sesión activa (nueva lógica con status 200)
    if (data.data && data.data.hasActiveSession) {
      await signOut(auth);

      // Verificar si todas las sesiones son recientes (< 24h)
      const allSessionsRecent = data.data.allSessionsRecent === true;

      // Mostrar alerta con opción de cerrar sesión remota o restaurar sesión
      const result = await Swal.fire({
        title: t("sessionActive"),
        text: allSessionsRecent
          ? t("allSessionsRecentMessage") ||
            "Todas sus sesiones son recientes (menos de 24 horas). Debe restaurar una sesión para poder iniciar."
          : `${t("sessionAlreadyActive")}`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: allSessionsRecent
          ? t("restoreSession") || "Restaurar Sesión"
          : t("closeRemoteSession"),
        cancelButtonText: t("cancel"),
        confirmButtonColor: brand.primary,
        cancelButtonColor: DIALOG_COLORS.cancel,
        allowOutsideClick: false,
        allowEscapeKey: true,
      });

      if (result.isConfirmed) {
        if (allSessionsRecent) {
          // Mostrar modal con las sesiones activas para que el usuario elija cuál cerrar
          const activeSessions = data.data.activeSessions || [];

          if (activeSessions.length === 0) {
            await Swal.fire({
              title: t("error"),
              text:
                t("noActiveSessionsFound") ||
                "No se encontraron sesiones activas",
              icon: "error",
              confirmButtonText: t("accept"),
              confirmButtonColor: brand.primary,
            });
            setError(t("noActiveSessionsFound"));
            setLoading(false);
            return;
          }

          // Crear HTML para mostrar las sesiones
          const sessionsHtml = activeSessions
            .map(
              (session: any, index: number) => `
            <div class="session-option" data-index="${index}" style="
              padding: 12px;
              margin: 8px 0;
              border: 2px solid #e5e7eb;
              border-radius: 8px;
              cursor: pointer;
              transition: all 0.2s;
            ">
              <div style="font-weight: bold; margin-bottom: 4px;">${session.device || "Unknown Device"}</div>
              <div style="font-size: 12px; color: #6b7280;">
                📍 ${session.location || "Unknown Location"}
              </div>
              <div style="font-size: 12px; color: #6b7280;">
                🕐 ${new Date(session.createdAt).toLocaleString()}
              </div>
            </div>
          `,
            )
            .join("");

          const sessionSelectionResult = await Swal.fire({
            title:
              t("selectSessionToClose") || "Selecciona una sesión para cerrar",
            html: `
              <div style="text-align: left; max-height: 300px; overflow-y: auto;">
                ${sessionsHtml}
              </div>
            `,
            icon: "question",
            showCancelButton: true,
            confirmButtonText:
              t("closeSelectedSession") || "Cerrar Sesión Seleccionada",
            cancelButtonText: t("cancel"),
            confirmButtonColor: brand.primary,
            cancelButtonColor: DIALOG_COLORS.cancel,
            allowOutsideClick: false,
            allowEscapeKey: true,
            didOpen: () => {
              // Agregar eventos de click a las opciones
              const options = document.querySelectorAll(".session-option");
              options.forEach((option) => {
                option.addEventListener("click", () => {
                  // Remover selección previa
                  options.forEach((opt) => {
                    (opt as HTMLElement).style.borderColor = "#e5e7eb";
                    (opt as HTMLElement).style.backgroundColor = "transparent";
                  });
                  // Seleccionar opción actual
                  (option as HTMLElement).style.borderColor = "#16a34a";
                  (option as HTMLElement).style.backgroundColor = "#f0fdf4";
                  (option as HTMLElement).dataset.selected = "true";
                });
              });
            },
            preConfirm: () => {
              const selectedOption = document.querySelector(
                '.session-option[data-selected="true"]',
              );
              if (!selectedOption) {
                Swal.showValidationMessage(
                  t("selectSessionRequired") ||
                    "Por favor selecciona una sesión",
                );
                return false;
              }
              return parseInt(
                (selectedOption as HTMLElement).dataset.index || "0",
              );
            },
          });

          if (sessionSelectionResult.isConfirmed) {
            const selectedIndex = sessionSelectionResult.value;
            const selectedSession = activeSessions[selectedIndex];

            // Mostrar estado de carga
            Swal.fire({
              title: t("closingSession") || "Cerrando sesión",
              allowOutsideClick: false,
              allowEscapeKey: false,
              didOpen: () => {
                Swal.showLoading();
              },
            });

            try {
              // Llamar al endpoint para cerrar la sesión específica
              await api.put(
                `${apiUrl}/session/close`,
                {},
                {
                  params: { id: selectedSession.sessionId },
                  headers: { Authorization: `Bearer ${data.data.token}` },
                },
              );

              // Mostrar confirmación de éxito
              await Swal.fire({
                title: t("sessionClosed") || "Sesión cerrada",
                text:
                  t("sessionClosedSuccess") ||
                  "La sesión ha sido cerrada exitosamente. Ahora puedes iniciar sesión.",
                icon: "success",
                confirmButtonText: t("accept"),
                confirmButtonColor: brand.primary,
              });

              // Permitir que el usuario intente iniciar sesión nuevamente
              setError(null);
              setLoading(false);
              return;
            } catch (error: any) {
              await Swal.fire({
                title: t("sessionCloseError") || "Error al cerrar sesión",
                text: error.response?.data?.message || t("unknownError"),
                icon: "error",
                confirmButtonText: t("accept"),
                confirmButtonColor: brand.primary,
              });
              setError(t("sessionCloseError"));
              setLoading(false);
              return;
            }
          }
        } else {
          // Mostrar estado de carga
          Swal.fire({
            title: t("sendingRequest"),
            allowOutsideClick: false,
            allowEscapeKey: false,
            didOpen: () => {
              Swal.showLoading();
            },
          });

          try {
            // Llamar al endpoint para solicitar reset de sesión
            const resetResponse = await api.post(
              `${apiUrl}/auth/request-session-reset`,
              { email },
              { timeout: 30000 },
            );

            if (resetResponse.status === 200) {
              // Mostrar confirmación de éxito
              await Swal.fire({
                title: t("sessionResetEmailSent"),
                text: t("sessionResetEmailSentText"),
                icon: "success",
                confirmButtonText: t("accept"),
                confirmButtonColor: brand.primary,
              });
            } else {
              throw new Error("Unexpected response");
            }
          } catch (error: any) {
            // Manejar errores específicos
            let errorMessage = t("sessionResetFailed");

            if (error.response?.status === 404) {
              errorMessage = t("sessionResetUserNotFound");
            } else if (error.response?.data?.message) {
              errorMessage = error.response.data.message;
            }

            await Swal.fire({
              title: t("sessionResetError"),
              text: errorMessage,
              icon: "error",
              confirmButtonText: t("accept"),
              confirmButtonColor: brand.primary,
            });
          }
        }
      }

      setError(t("sessionAlreadyActive"));
      setLoading(false);
      return;
    }

    if (data.data && data.data.token) {
      localStorage.setItem("token", data.data.token);
      // Ya hay sesión de consola: la de Firebase deja de estar huérfana.
      firebaseSessionOpen = false;

      if (data.data.sessionId) {
        localStorage.setItem("sessionId", data.data.sessionId);
      }

      clearSandboxView();
      if (data.data.companyId) {
        localStorage.setItem("userCompanyId", data.data.companyId);
      }

      if (data.data.userId) {
        localStorage.setItem("userId", data.data.userId);
      }

      // 2FA: la cuenta tiene un segundo paso. La sesión ya existe pero el
      // backend no le deja pedir nada hasta dar el código, así que se pide
      // ANTES de leer usuario y rol. Si cierra el diálogo sin darlo, el propio
      // diálogo cierra la sesión a medias.
      clearSecurityStatus();
      if (data.data.twoFactorRequired) {
        const verified = await requestSecurityChallenge({ purpose: "login" });
        if (!verified) {
          setLoading(false);
          return;
        }
      }

      // El backend manda el perfil (usuario, rol y compañía) en la respuesta
      // del login. Uno anterior no lo manda: se piden por separado.
      const profile: LoginProfile =
        data.data.profile ??
        (await fetchLegacyProfile(
          userCredential.user.email,
          data.data.token,
          data.data.companyId,
        ));
      let userDataFinal = profile.user ?? data.user ?? data.data;
      const roleName = profile.roleName || "";

      if (roleName && userDataFinal?.role_id) {
        localStorage.setItem("userRole", roleName);
        localStorage.setItem("userRoleId", userDataFinal.role_id);

        // Guardar yard_ids para supervisores y vigilantes (no admins - they can have multiple yards)
        if (
          userDataFinal.role_id === TRUCK_YARD_SUPERVISOR_ROLE_ID ||
          userDataFinal.role_id === TRUCK_YARD_GUARD_ROLE_ID ||
          userDataFinal.role_id === TRUCK_YARD_INSPECTOR_ROLE_ID
        ) {
          const yardIds = userDataFinal.yard_ids || [];
          const yardId = userDataFinal.yard_id || "";
          localStorage.setItem("userYardIds", JSON.stringify(yardIds));
          localStorage.setItem("userYardId", yardId);

          // Obtener y guardar el nombre del patio
          if (yardId) {
            try {
              const yardRes = await api.get("/truck-yard/yards", {
                params: { id: yardId },
                headers: {
                  Authorization: `Bearer ${data.data.token}`,
                },
              });
              const yardData = yardRes.data?.data;
              if (yardData?.name) {
                localStorage.setItem("userYardName", yardData.name);
              }
            } catch (error) {
              console.error("Error fetching yard name:", error);
            }
          }
        }

        // Si NO es admin_reseller, auto-asignar su compañía como companyId
        if (
          !isPlatformAdminRoleId(userDataFinal.role_id) &&
          data.data.companyId
        ) {
          localStorage.setItem("companyId", data.data.companyId);
        }
      }

      if (userDataFinal?.role_id && data.data.companyId && profile.companyName) {
        localStorage.setItem("userCompanyName", profile.companyName);
      }
      // Marca de la compañía para PostHog (CC-CONSOLE#1538).
      setPostHogBrand(profile.companyBrand);

      // Add company_id to userDataFinal for reactive store access
      if (data.data.companyId && userDataFinal) {
        userDataFinal.company_id = data.data.companyId;
      }

      const loggedUserId =
        userDataFinal?.id || data.data.userId || localStorage.getItem("userId");
      let destination = resolvePostLoginRoute(
        userDataFinal?.role_id || "",
        roleName,
        data.data.companyId || "",
      );
      // Llegó con `next` (p. ej. abrió /dispositivos/vincular sin sesión,
      // CC-CONSOLE#1561): ahí vuelve, también después del 2FA. La ruta del
      // rol queda como respaldo.
      if (getNextPath()) {
        const { resolveNextOr } = await import("../onboarding/onboardingService");
        destination = await resolveNextOr(destination);
      }

      // Los accesos que ya vienen en la respuesta del login se cargan ANTES de
      // `setUser`: el arranque de la app (useAccessBootstrap) los encuentra
      // listos y no los vuelve a pedir, y al llegar al destino no hay segundo
      // spinner (AccessProtectedRoute / hub esperan `isResolved`).
      const embeddedAccess = profile.access;
      if (loggedUserId && embeddedAccess) {
        useAccessStore
          .getState()
          .hydrate(loggedUserId, normalizeAccessLayers(embeddedAccess));
      }

      setUser(userDataFinal);
      // Evento `login` estandar de GA4. No lleva rol ni empresa: identity.ts ya
      // los fija como propiedades de persona; repetirlos por evento es ruido.
      trackLoginSucceeded(method);

      // Antes de navegar se deja listo lo que el destino necesita: sus chunks
      // y, si NO vinieron en el login (backend anterior, 2FA o fallo), los
      // accesos. Se espera por todo a la vez, como MÁXIMO `ACCESS_WAIT_MS`; si
      // algo no llega, se navega igual y la pantalla muestra su loader. `load`
      // captura sus errores (status "failed"): no rechaza.
      const warmup: Promise<unknown>[] = [preloadRouteChunks(destination)];
      if (loggedUserId && !embeddedAccess) {
        warmup.push(useAccessStore.getState().load(loggedUserId));
      }
      await waitAtMost(Promise.all(warmup), ACCESS_WAIT_MS);

      navigate(destination);
    } else {
      if (data.meta?.error || data.error) {
        showAlert({
          title: t("errorLogin"),
          text: data.meta?.message || data.message || t("unknownError"),
          icon: "error",
          confirmButtonText: t("accept"),
        });
      }
      trackLoginFailed(method, "backend_no_token");
      setError(t("errorLogin"));
    }
  } catch (err) {
    // Manejar error específico de compañía suspendida.
    //
    // La señal es `meta.code === "COMPANY_SUSPENDED"` (+ 403): el errorHandler
    // del backend TRADUCE `meta.message`, así que la clave `companySuspended`
    // casi nunca viaja por el cable y la comparación por texto no detectaba
    // nada. Se conserva como respaldo para un backend anterior.
    if (err && typeof err === "object" && "response" in err) {
      const axiosError = err as any;
      if (
        isCompanySuspendedError(err) ||
        (axiosError.response?.status === 403 &&
          axiosError.response?.data?.meta?.message === "companySuspended")
      ) {
        // `signInWithEmailAndPassword` ya creó la sesión de FIREBASE antes de
        // que /login respondiera 403: se cierra aquí, explícito. Sin esto el
        // usuario de una compañía suspendida se queda autenticado en Firebase.
        await signOut(auth).catch(() => undefined);
        firebaseSessionOpen = false;

        await Swal.fire({
          title: "Acceso Denegado",
          text: "No puede acceder a su cuenta debido a que la compañía se encuentra suspendida. Para más atención consulte con soporte.",
          icon: "warning",
          confirmButtonText: "Entendido",
          confirmButtonColor: brand.primary,
          allowOutsideClick: false,
          allowEscapeKey: false,
        });

        trackLoginBlocked("company_suspended");
        setError("Compañía suspendida");
        setLoading(false);
        return;
      }
    }

    // Red de seguridad para el resto de fallos (500 del backend, red caída, un
    // 401/403 de credenciales revocadas…): login que no termina bien no deja
    // sesión de Firebase viva.
    if (firebaseSessionOpen) {
      await signOut(auth).catch(() => undefined);
    }

    const errorMessages: Record<string, string> = {
      "auth/invalid-email": t("invalidEmail"),
      "auth/user-not-found": t("userNotFound"),
      "auth/wrong-password": t("wrongPassword"),
      "auth/invalid-credential": t("invalidCredential"),
      "auth/network-request-failed": t("networkRequestFailed"),
      "auth/email-already-in-use": t("emailAlreadyInUse"),
      "auth/weak-password": t("weakPassword"),
      "auth/too-many-requests": t("tooManyRequests"),
    };
    // Se reporta el CODIGO (`auth/wrong-password`, `auth/too-many-requests`),
    // nunca `errorMessage`: ese ya esta traducido y puede interpolar datos del
    // patio, del turno o del dispositivo de la sesion abierta.
    if (err instanceof FirebaseError) {
      trackLoginFailed(method, err.code);
    } else if (axios.isAxiosError(err)) {
      trackLoginFailed(method, "http_error", err.response?.status);
    } else {
      trackLoginFailed(method, "unknown");
    }

    let errorMessage: string;
    if (err instanceof FirebaseError) {
      errorMessage =
        errorMessages[err.code as keyof typeof errorMessages] ??
        t("unknownError");
    } else if (axios.isAxiosError(err) && err.response?.data) {
      // Mensaje del backend: meta.message (formato del errorHandler) o message directo
      const data = err.response.data as {
        meta?: { message?: string };
        message?: string;
      };
      errorMessage = data.meta?.message ?? data.message ?? t("unknownError");
    } else {
      errorMessage = t("unknownError");
    }

    showAlert({
      title: t("errorLogin"),
      text: errorMessage,
      icon: "error",
      confirmButtonText: t("accept"),
    });
    setError(errorMessage);
  } finally {
    setLoading(false);
  }
};

export const toggleSwitch = (
  language: string,
  setIsEnglish: React.Dispatch<React.SetStateAction<boolean>>,
  i18n: I18nType,
) => {
  try {
    i18n.changeLanguage(language);
    setIsEnglish(language === "en");
    localStorage.setItem("@language", language);
  } catch (error) {
    console.error(error);
  }
};

export const resetPassword = async (
  emailOrUsername: string,
  showAlert: (alert: CustomAlertProps) => void,
  t: TFunction<"translation", undefined>,
): Promise<boolean> => {
  try {
    let email = emailOrUsername;

    // Si no es un email, buscar el email asociado al username
    if (!isEmail(emailOrUsername)) {
      const fetchedEmail = await getEmailFromUsername(emailOrUsername);
      if (!fetchedEmail) {
        showAlert({
          title: t("resetPasswordError"),
          text: t("userNotFoundForReset"),
          icon: "error",
          confirmButtonText: t("accept"),
        });
        return false;
      }
      email = fetchedEmail;
    }

    await sendPasswordResetEmail(auth, email);

    trackPasswordResetRequested(false);

    showAlert({
      title: t("resetPasswordSuccess"),
      text: t("resetPasswordEmailSent"),
      icon: "success",
      confirmButtonText: t("accept"),
    });
    return true;
  } catch (err) {
    const errorMessages: Record<string, string> = {
      "auth/invalid-email": t("invalidEmail"),
      "auth/user-not-found": t("userNotFound"),
      "auth/too-many-requests": t("tooManyRequests"),
    };

    let errorMessage = t("unknownError");
    if (err instanceof FirebaseError) {
      errorMessage =
        errorMessages[err.code as keyof typeof errorMessages] ||
        t("unknownError");
    }

    // Solo se reporta el caso de limite de intentos. Los demas fallos NO se
    // emiten a proposito: `auth/user-not-found` revelaria por analitica que un
    // correo existe o no, que es justo la enumeracion de cuentas que el flujo
    // de recuperacion evita.
    if (err instanceof FirebaseError && err.code === "auth/too-many-requests") {
      trackPasswordResetRequested(true);
    }

    showAlert({
      title: t("resetPasswordError"),
      text: errorMessage,
      icon: "error",
      confirmButtonText: t("accept"),
    });
    return false;
  }
};
